import { createFeatureSelector, createSelector } from "@ngrx/store";
import { WidgetState, FeatureKey } from "../reducers";
import { getRouterSelectors } from "@ngrx/router-store";
const {selectQueryParam} = getRouterSelectors();
import * as _ from 'lodash';
import { DatesAndAccupancy, HotelBookingData, HotelBookingDataDetails, NO_IMAGE } from "../index.types";
import moment from "moment";
import { BestPriceSorter, BuildSearchParams } from "app/helpers";
import { isDevMode } from "@angular/core";

export const WidgetFeature = createFeatureSelector<WidgetState>(FeatureKey);

export const selectWidgetData    = createSelector(WidgetFeature, (state: WidgetState) => state.widgetData);
export const selectWHasComment   = createSelector(WidgetFeature, (state: WidgetState) => !!state.widgetData?.detail.scwHasComment);
export const selectWHasBook      = createSelector(WidgetFeature, (state: WidgetState) => !!state.widgetData?.detail.scwHasBook);
export const selectListOta       = createSelector(WidgetFeature, (state: WidgetState) => state.widgetData?.detail.listOta);
export const selectClientCountry = createSelector(WidgetFeature, (state: WidgetState) => state.widgetData?.country);

export const selectTranslations         = createSelector(WidgetFeature, (state: WidgetState) => ({...state.translations['static'], ...state.translations['staticaddvalue']}));
export const selectRoomsNameTranslation = createSelector(WidgetFeature, (state: WidgetState) => state.translations['Chamber']);
export const selectTranslation          = (props: {key: string})=> createSelector(selectTranslations, (translations) => translations[props.key]);
export const selectRoomNameTranslation  = (props: {roomId: number})=> createSelector(selectRoomsNameTranslation, (translations) => translations[`${props.roomId}-titer`]);

export const selectDatesAndAccupancy        = createSelector(WidgetFeature, (state: WidgetState) => state.datesAndAccupancy);
export const selectDatesAndAccupancyRange   = createSelector(WidgetFeature, (state: WidgetState) => state.datesAndAccupancy?.range);
export const selectDatesAndAccupancyResult  = createSelector(WidgetFeature, (state: WidgetState) => {
    const _parts = {
        from: moment(state.datesAndAccupancy?.range.from).format('DD-MM-YYYY'),
        to: moment(state.datesAndAccupancy?.range.to).format('DD-MM-YYYY'),
        adultes: state.datesAndAccupancy?.adultes,
        children: state.datesAndAccupancy?.children,
        ages: state.datesAndAccupancy?.ages.join(', '),
        rooms: state.datesAndAccupancy?.rooms
    }
    return (state.datesAndAccupancy)? _parts  : null
});

export const selectCurrentLang  = selectQueryParam('lang');
export const selectComments     = createSelector(WidgetFeature, selectCurrentLang, (state: WidgetState, lang) => {
    const _filtredCommentInCurrentLang = state.comments?.filter((c: any)=>c.commentLang == lang)
    return (_filtredCommentInCurrentLang?.length)? _filtredCommentInCurrentLang : state.comments?.filter((c: any)=>c.commentLang == 'en')
});

export const selectHotelBookingData     = createSelector(WidgetFeature, (state: WidgetState) => {
    if(state.hotelBookingData){
        // keep just one partner offer
            const minPartner         = Math.min(...Object.values(state.hotelBookingData).map(obj => ( obj.bestPrices.partner )? obj.bestPrices.partner.ttc : 1e9));
            const _hotelBookingData  = Object.entries(state.hotelBookingData).map((hbd)=>{
                const _clonedHbd = _.cloneDeep(hbd)
                const [engine, hotelBookingData] = hbd
                //🚨 hide partner offers in production, this is why i add isDevMode condition
                if(hotelBookingData.bestPrices.partner?.ttc != minPartner || !isDevMode()){
                    _clonedHbd[1].bestPrices.partner = null
                }
                return _clonedHbd
            });
        // -------------
        const _sortedHotelBookingData   = _hotelBookingData.sort(([engine, hbd])=>hbd.bestPrices.googleHotelAds!=null?-1:1)
        return _sortedHotelBookingData
    }
    return null
});
export const selectHotelBookingLoader   = createSelector(WidgetFeature, (state: WidgetState) => state.loaders.engines);
export const selectEngine               = (props: {engine: string})=> createSelector(WidgetFeature, (state: WidgetState) => state.hotelBookingData?.[props.engine]);

export const selectBestPrice            = createSelector(WidgetFeature, selectHotelBookingData, selectDatesAndAccupancy, (state: WidgetState, hotelBookingData, datesAndAccupancy) => {
    const bestPricesArray = _.flatten(hotelBookingData?.map(
                                ([engine, data])=>{
                                    const _arr = [
                                        data.bestPrices.bestprice,
                                        data.bestPrices.googleHotelAds,
                                        data.bestPrices.partner
                                    ];
                                    return _arr.filter(Boolean)
                                } 
                            ));
    const sortedArray = bestPricesArray.sort((a, b)=> BestPriceSorter(a as HotelBookingDataDetails, b as HotelBookingDataDetails, datesAndAccupancy as DatesAndAccupancy))
    return sortedArray.shift() || null
});

export const selectBestPriceMealPLan = createSelector(selectBestPrice, (bestPrice)=>{
    const mealplan = bestPrice?.addedvalues.filter(a=>a.category=='mealplan');
    return (mealplan && mealplan.length > 0)? mealplan[0] : bestPrice?.addedvalues[0]
}) 

export const selectHotelBookingNoAvailability = createSelector(WidgetFeature, (state: WidgetState) => state.noAvailability);

export const selectProposition       = createSelector(WidgetFeature, (state: WidgetState) => state.proposition);
export const selectPropositionLoader = createSelector(WidgetFeature, (state: WidgetState) => state.loaders.proposition);

export const selectOrigin       = selectQueryParam('origin');
export const selectIsMobile     = selectQueryParam('isMobile');
export const selectBookingLink  = createSelector(selectWidgetData, selectDatesAndAccupancy, selectOrigin, selectCurrentLang, (widgetData, datesAndAccupancy, origin, lang) => {
    const start = datesAndAccupancy?.range.from.toString('YMD','-');
    const end   = datesAndAccupancy?.range.to.toString('YMD','-');
    const etabName = widgetData?.hotel;
    const agesenfants = (datesAndAccupancy?.ages)? datesAndAccupancy?.ages : undefined;
    
    const searchParams = {
        idetab: widgetData?.id_hotel_in_bo,
        adults: datesAndAccupancy?.adultes,
        enfants: datesAndAccupancy?.children,
        start_date: start,
        end_date: end,
        agesenfants
    };
    
    const BP_searchParams = {
        adults: datesAndAccupancy?.adultes,
        enfants: datesAndAccupancy?.children,
        currency: datesAndAccupancy?.currency,
        start,
        end,
        idetab: widgetData?.id_hotel_in_bo,
        city: "18",
        agesenfants
    }
     
    return [
        `http://thebookingbutton.site/${lang}/availibility/${etabName}?${BuildSearchParams(BP_searchParams)}`,
        `${origin}/${lang}/availibility?${BuildSearchParams(searchParams)}`
    ]
});

export const selectMainImage = (props: {code_room: number})=> createSelector(WidgetFeature, (state: WidgetState) => {
    const _img = state.imagesData?.rooms.filter(r=>r.id_chambre == props.code_room)?.[0]?.img[0];
    return _img ? `${state.imagesData?.baseurl}${_img}` : NO_IMAGE
});

//** https://rate.picscache.com/images/35096/upload/300X199/${_img}

export const selectSearchPanelVisibility = createSelector(WidgetFeature, (state: WidgetState) => state.searchPanelVisibility);

//Detect if many options selector
export const selectEngineOptions = createSelector(selectHotelBookingData, (hotelBookingData) => {
    const engineOptions = _.union( _.flatten(hotelBookingData?.map(
                                ([engine, data])=>{
                                    return Object.keys(data.engine)
                                } 
                            )) );
    return engineOptions
});